import './SessionPage.css';
import { MER_EVSE_ID, MER_LOCATION_NAME, MER_ORDER_ID, MER_STOP_PRESSED } from "../Start/StartPage";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import useElapsedTime from "../../Common/useElapsedTime";
import { socket } from "../../App";
import { Socket } from "socket.io-client";
import { OrderSession } from "../../Model/Session";
import { getPaymentStatus, getSession, stopSession } from "../../Api/Api";
import ErrorPage, { ERROR_PAGE_TYPE } from "../Error/ErrorPage";
import { Payment } from "../../Model/Payment";
import {
    registerErrorCharger, registerErrorChargerStart,
    registerPaymentCancel,
    registerPaymentSuccess,
    registerStopClicked
} from "../../Api/GTM";
import useInterval from "../../Common/UseInterval";
import SlideToStop from "./SlideToStop";
import Spinner from "../../Components/Spinner";
import ChargerIcon from "../../assets/charger.svg";
import { isMockMode } from "../../Common/MockMode";
import { formatTariff } from "../../Common/Tariff";

export const SESSION_PAYMENT_SUCCESS_SENT = "SESSION_PAYMENT_SUCCESS_SENT";

// Stands still at the fill's starting level, with a spinner instead of the bolt, until charging begins
function ChargingIcon({ charging }: { charging: boolean }) {
    return (
        <div className={"charge-battery-wrap" + (charging ? "" : " charge-battery-wrap--idle")}>
            <div className="charge-battery-cap" />
            <div className="charge-battery-body">
                <div className="charge-battery-chamber">
                    <div className="charge-battery-fill">
                        <div className="charge-battery-col charge-battery-col--1" />
                        <div className="charge-battery-col charge-battery-col--2" />
                        <div className="charge-battery-col charge-battery-col--3" />
                    </div>
                    {charging ?
                        <svg className="charge-battery-bolt" viewBox="0 0 24 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M9.46854 0H20.5313L14.509 11.5589H24L3.51702 40L10.5885 18.8389H0L9.46854 0Z" fill="white" />
                        </svg>
                        :
                        <span className="charge-battery-spinner">
                            <Spinner className="charge-battery-spinner-svg"/>
                        </span>
                    }
                </div>
            </div>
        </div>
    );
}

export default function SessionPage() {
    const [orderSession, setOrderSession] = useState<{ orderSession: OrderSession | undefined, received: Date }>({ orderSession: undefined, received: new Date() });
    const history = useNavigate();
    const [currentSocket, setCurrentSocket] = useState<Socket | undefined>(undefined)
    const [error, setError] = useState(ERROR_PAGE_TYPE.NONE)
    const [stopClicked, setStopClicked] = useState(false)


    let { orderId } = useParams<{ orderId: string }>();
    if (!orderId) {
        const ord = localStorage.getItem(MER_ORDER_ID);
        orderId = ord ? ord : "";
    } else {
        localStorage.setItem(MER_ORDER_ID, orderId)
    }

    if (!orderId) {
        history("/vipps/" + localStorage.getItem(MER_EVSE_ID));
    }

    const onFocus = () => {
        if (window.location.href.includes("/session/active") &&
            (error === ERROR_PAGE_TYPE.NONE || error === ERROR_PAGE_TYPE.PAYMENT_INITIAL)) {
            window.location.reload();
        }
    };

    useEffect(() => {
        window.addEventListener('focus', onFocus);
        return () => {
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('pageshow', onFocus);
        };
    });


    // Will keep track of when the last update was received. Updates should be received every 30 second - if more than
    // 45 seconds has passed, refresh the page
    useInterval(() => {
        const dif = new Date().getTime() - orderSession.received.getTime();
        const secondsSinceLastSessionUpdate = Math.abs(dif / 1000);
        if (secondsSinceLastSessionUpdate > 45) {
            if (window.location.href.includes("/session/active") &&
                (error === ERROR_PAGE_TYPE.NONE || error === ERROR_PAGE_TYPE.PAYMENT_INITIAL)) {
                window.location.reload();
            }
        }
    }, 5000);

    useEffect(() => {
        const stopped = localStorage.getItem(MER_STOP_PRESSED);
        if (stopped) {
            setStopClicked(true);
            if (isMockMode() && orderId) {
                setTimeout(() => {
                    history(`/vipps/receipt/${orderId}`);
                }, 1000);
            }
        }
        getOrderSession(orderId)
        // getOrderSession and history are re-created every render; including them would re-fire
        // this effect (and the session request) on every state update.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId])


    useEffect(() => {
        setCurrentSocket(socket.on("session", (data: OrderSession) => {
            handleUpdatedSession(data)
        }));
        socket.emit("registerOrder", orderId);
        // handleUpdatedSession is re-created every render; including it would re-register the
        // socket listener on every state update.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSocket, orderId])


    function getOrderSession(orderId?: string) {
        if (orderId) {
            getSession(orderId).then((response) => {
                if (!handleUpdatedSession(response.data)) {
                    checkPayment(orderId);
                }
            })
                .catch(function (errorMessage) {
                    checkPayment(orderId);
                });
        }
    }

    // Without a session to show, the payment tells whether the start failed, the
    // driver aborted in Vipps, or the charger has just not started yet
    function checkPayment(orderId: string) {
        getPaymentStatus(orderId).then(res => {
            const payment = res.data as unknown as Payment;
            if (payment.transactionLogHistory.find(p => p.operation === 'VOID')) {
                registerErrorChargerStart();
                setError(ERROR_PAGE_TYPE.CHARGER);
            }
            else if (payment.transactionLogHistory.find(p => p.operation === 'CANCEL')) {
                registerPaymentCancel();
                returnToStartAfterCancel();
            } else {
                setError(ERROR_PAGE_TYPE.PAYMENT_INITIAL)
            }
        }).catch(function (error) {
            setError(ERROR_PAGE_TYPE.SESSION_NOT_FOUND)
        })
    }

    // Nothing was paid, so there is nothing to wait for: straight back to the
    // connector list, which says the payment was aborted
    function returnToStartAfterCancel() {
        localStorage.removeItem(MER_ORDER_ID);
        if (orderId) {
            stopSession(orderId).catch(() => {});
        }
        history(`/vipps/${localStorage.getItem(MER_EVSE_ID) || ""}`, {state: {paymentCancelled: true}});
    }

    function navigateToStart() {
        if (orderId) {
            stopSession(orderId).then(res => {
                const evseId = localStorage.getItem(MER_EVSE_ID) || "";
                history(`/vipps/${evseId}`)
            }
            ).catch(e => {
                const evseId = localStorage.getItem(MER_EVSE_ID) || "";
                history(`/vipps/${evseId}`)
            });
        }
    }

    // Returns false for a status it does not know, which says nothing about the order
    function handleUpdatedSession(session: OrderSession): boolean {
        const status = session.session.status;
        if (!['ACTIVE', 'UPDATED', 'COMPLETED', 'CANCELLED', 'FAILED'].includes(status)) {
            console.log(`Unknown session status: ${status}`)
            return false;
        }
        if (session.session && session.session.connector) {
            session.session.connector.tariff = session.session.connector.tariff.replace("0.0kr/min+", "");
            session.session.connector.tariff_Unreg = session.session.connector.tariff_Unreg.replace("0.0kr/min+", "");
        }
        // Send payment success only once
        if (!localStorage.getItem(SESSION_PAYMENT_SUCCESS_SENT)) {
            localStorage.setItem(SESSION_PAYMENT_SUCCESS_SENT, "true");
            registerPaymentSuccess();
        }
        if (status === 'ACTIVE' || status === 'UPDATED') {
            const startDate = session.session.startDate ? session.session.startDate :
                (session.session as any).start_date_time;
            // Kept even without a start date, so the page can show the connector while waiting to start
            setError(ERROR_PAGE_TYPE.NONE);
            session.session.startDate = startDate;
            setOrderSession({ orderSession: session, received: new Date() });
        } else if (status === 'COMPLETED') {
            history(`/vipps/receipt/${orderId}`)
        } else if (status === 'CANCELLED') {
            setError(ERROR_PAGE_TYPE.VIPPS);
        } else {
            registerErrorCharger();
            setError(ERROR_PAGE_TYPE.CHARGER)
        }
        return true;
    }

    function stop() {
        if (orderId) {
            stopSession(orderId).then(res => {
                if (res.status === 200) {
                    registerStopClicked();
                    localStorage.setItem(MER_STOP_PRESSED, "stopped")
                    setStopClicked(true);
                    if (isMockMode()) {
                        setTimeout(() => {
                            history(`/vipps/receipt/${orderId}`);
                        }, 6000);
                    }
                }
            });
        }
    }

    const { timeElapsed } = useElapsedTime(orderSession.orderSession?.session.startDate || orderSession.received);

    // Paid, but the charger has not started yet
    const waiting = error === ERROR_PAGE_TYPE.PAYMENT_INITIAL ||
        (!!orderSession.orderSession && !orderSession.orderSession.session.startDate);

    if (!waiting && error !== ERROR_PAGE_TYPE.NONE) {
        return <ErrorPage errorType={error} />
    }

    const locationName = localStorage.getItem(MER_LOCATION_NAME) || "";

    // Looking up the order can take a while, e.g. coming back from Vipps
    if (!waiting && !orderSession.orderSession) {
        return (
            <div className="charge-app">
                <header className="charge-topbar">
                    <h2>{locationName}</h2>
                </header>
                <div className="charge-body charge-checking">
                    <Spinner label="Sjekker betalingen"/>
                    <p className="charge-waiting-text">Sjekker betalingen …</p>
                </div>
            </div>
        );
    }

    // Before the session exists there is no connector to show yet
    const session = orderSession.orderSession?.session;
    const connector = session?.connector;

    return (
        <div className="charge-app">
            <header className="charge-topbar">
                <h2>{locationName}</h2>
            </header>

            {connector &&
                <div className="charge-connector-card">
                    <img src={ChargerIcon} className="charge-connector-icon" alt="" />
                    <div>
                        <h3>Ladeuttak {connector.smsCode}</h3>
                        <p>Drop-in pris: {formatTariff(connector.tariff_Unreg)}</p>
                    </div>
                </div>
            }

            <div className="charge-body">
                <div className="charge-visual">
                    <div className="charge-status">
                        <p className="charge-label">{waiting ? "Starter lading" : "Lading pågår"}</p>
                    </div>
                    <ChargingIcon charging={!waiting}/>
                </div>

                {waiting ?
                    <div className="charge-waiting">
                        <p className="charge-waiting-text">Vi venter på at laderen bekrefter oppstarten. Dette kan ta litt tid.</p>
                    </div>
                    :
                    <div className="charge-stats">
                        <div className="charge-stat">
                            <span className="charge-stat-value">{session!.kwh} kWh</span>
                            <span className="charge-stat-label">Energi mottatt</span>
                        </div>
                        <div className="charge-stat">
                            <span className="charge-stat-value">{timeElapsed}</span>
                            <span className="charge-stat-label">Ladetid</span>
                        </div>
                    </div>
                }
            </div>

            <div className="session-stop-box">
                {waiting ?
                    <button type="button" className="charge-cancel" onClick={() => navigateToStart()}>Avbryt</button>
                    :
                    <SlideToStop label="Stopp lading" confirmingLabel="Lading stopper..."
                                 confirming={stopClicked} onConfirm={() => stop()}/>
                }
            </div>
        </div>
    );
}
